import { MenuItemButton } from '@/components/layout/DesktopNavbar';
import { roundedClassname, roundedDropdownClassname } from '@/helpers/environmentHelper';
import { useAppDispatch } from '@/state/hooks';
import { setXplorSpotWorkspaceTabLayout, setXplorTraderWorkspaceTabLayout } from '@/state/reducers/workspaceSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { XplorSpotModules } from '@/utils/hooks/useXplorSpotLayout';
import { XplorTraderModules } from '@/utils/hooks/useXplorTraderLayout';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { Menu, Transition } from '@headlessui/react';
import cn from 'classnames';
import { Fragment, useCallback, useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import { v4 as uuidv4 } from 'uuid';

export interface AddModuleProps {
    tabList: string[];
    tabIcon: Record<XplorSpotModules & XplorTraderModules, IconType>;
    platform: 'xplortrader' | 'xplorspot';
    hasBg?: boolean;
    hasBorders?: boolean;
    isIconOnly?: boolean;
    onClick?: (tab) => void;
}

const AddModule = ({ tabList, tabIcon, platform, hasBorders = true, isIconOnly = false, onClick }: AddModuleProps) => {
    const dispatch = useAppDispatch();

    const { mobile, tablet, interim } = useScreenSizes();
    const isMobile = useMemo(() => mobile || tablet || interim, [mobile, tablet, interim]);
    const lang = useLanguage();

    const getModuleTitle = useCallback(
        (module) => {
            if (platform === 'xplortrader') {
                switch (module as XplorTraderModules) {
                    case 'Tickers':
                        return lang.modules.tickers;
                    case 'Account Value':
                        return lang.modules['account-value'];
                    case 'Chart':
                        return lang.modules.chart;
                    case 'Order Book':
                        return lang.modules['order-book'];
                    case 'Blotter':
                        return lang.modules.blotter;
                    case 'Markets':
                        return lang.modules.markets;
                    default:
                        return '';
                }
            } else {
                switch (module as XplorSpotModules) {
                    case 'Order':
                        return lang.modules.order;
                    case 'Balances':
                        return lang.modules.balances;
                    case 'Chart':
                        return lang.modules.chart;
                    case 'Order Book':
                        return lang.modules['order-book'];
                    case 'Blotter':
                        return lang.modules.blotter;
                    case 'Markets':
                        return lang.modules.markets;
                    default:
                        return '';
                }
            }
        },
        [lang]
    );

    const addModuleToXplorSpotWorkspace = useCallback(
        (module) => {
            dispatch(setXplorSpotWorkspaceTabLayout({ id: 'current', layout: module, screen: 'desktop' }));
        },
        [dispatch]
    );

    const addModuleToXplorTraderWorkspace = useCallback(
        (module) => {
            dispatch(setXplorTraderWorkspaceTabLayout({ id: 'current', layout: module, screen: 'desktop' }));
        },
        [dispatch]
    );

    return (
        <Menu as="div" className={cn('relative inline-block text-left', roundedClassname)} data-onboarding="add-module">
            <Menu.Button
                type="button"
                className={cn(
                    'relative flex gap-1 text-sm items-center transition duration-150 ease-in-out justify-between h-full rounded-md text-brand-text bg-brand-background-dark',
                    roundedClassname,
                    {
                        'border border-brand-neutral-700 p-1 rounded-md hover:border-brand-neutral-600': hasBorders,
                        'pl-1.5 pr-2.5': !isMobile && hasBorders && lang.locale !== 'ar',
                        'pl-2.5 pr-1.5': !isMobile && hasBorders && lang.locale === 'ar',
                        'py-2': !hasBorders,
                        'px-2 py-2': isMobile,
                        'py-2 pl-2 pr-3 bg-brand-neutral-700 hover:bg-brand-neutral-600 border-none': !isIconOnly
                    }
                )}>
                {!isIconOnly && (
                    <Fragment>
                        <div className="w-5 h-5">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-ds-icon-button-xs ms-ds-0 me-ds-0 mt-ds-0 mb-ds-0"
                                name="LayoutAdd">
                                <path
                                    d="M12.05 21.25C12.6023 21.25 13.05 20.8023 13.05 20.25C13.05 19.6977 12.6023 19.25 12.05 19.25V21.25ZM4.84202 20.032L5.29601 19.141L5.29601 19.141L4.84202 20.032ZM3.96799 19.158L4.85899 18.704L4.85899 18.704L3.96799 19.158ZM20.032 4.84202L19.141 5.29601L19.141 5.29601L20.032 4.84202ZM19.158 3.96799L18.704 4.85899L18.704 4.85899L19.158 3.96799ZM3.96799 4.84202L3.07698 4.38803L3.07698 4.38803L3.96799 4.84202ZM4.84202 3.96799L4.38803 3.07698L4.38803 3.07698L4.84202 3.96799ZM10.75 3.75V2.75H8.75V3.75H10.75ZM8.75 20.25V21.25H10.75V20.25H8.75ZM19.25 6.95V11.5H21.25V6.95H19.25ZM12.05 19.25H6.95V21.25H12.05V19.25ZM4.75 17.05V6.95H2.75V17.05H4.75ZM6.95 4.75H17.05V2.75H6.95V4.75ZM6.95 19.25C6.37345 19.25 6.00117 19.2492 5.71784 19.2261C5.44617 19.2039 5.34545 19.1662 5.29601 19.141L4.38803 20.923C4.76641 21.1158 5.15963 21.1871 5.55497 21.2194C5.93864 21.2508 6.40645 21.25 6.95 21.25V19.25ZM2.75 17.05C2.75 17.5935 2.74922 18.0614 2.78057 18.445C2.81287 18.8404 2.88419 19.2336 3.07698 19.612L4.85899 18.704C4.8338 18.6545 4.79612 18.5538 4.77393 18.2822C4.75078 17.9988 4.75 17.6265 4.75 17.05H2.75ZM5.29601 19.141C5.10785 19.0451 4.95487 18.8921 4.85899 18.704L3.07698 19.612C3.3646 20.1765 3.82354 20.6354 4.38803 20.923L5.29601 19.141ZM21.25 6.95C21.25 6.40645 21.2508 5.93864 21.2194 5.55497C21.1871 5.15963 21.1158 4.76641 20.923 4.38803L19.141 5.29601C19.1662 5.34545 19.2039 5.44617 19.2261 5.71784C19.2492 6.00117 19.25 6.37345 19.25 6.95H21.25ZM17.05 4.75C17.6266 4.75 17.9988 4.75078 18.2822 4.77393C18.5538 4.79612 18.6545 4.8338 18.704 4.85899L19.612 3.07698C19.2336 2.88419 18.8404 2.81287 18.445 2.78057C18.0614 2.74922 17.5936 2.75 17.05 2.75V4.75ZM20.923 4.38803C20.6354 3.82354 20.1765 3.3646 19.612 3.07698L18.704 4.85899C18.8922 4.95487 19.0451 5.10785 19.141 5.29601L20.923 4.38803ZM4.75 6.95C4.75 6.37345 4.75078 6.00117 4.77393 5.71784C4.79612 5.44617 4.8338 5.34545 4.85899 5.29601L3.07698 4.38803C2.88419 4.76641 2.81287 5.15963 2.78057 5.55497C2.74922 5.93864 2.75 6.40645 2.75 6.95H4.75ZM6.95 2.75C6.40645 2.75 5.93864 2.74922 5.55497 2.78057C5.15963 2.81287 4.76641 2.88419 4.38803 3.07698L5.29601 4.85899C5.34545 4.8338 5.44617 4.79612 5.71784 4.77393C6.00117 4.75078 6.37345 4.75 6.95 4.75V2.75ZM4.85899 5.29601C4.95487 5.10785 5.10785 4.95487 5.29601 4.85899L4.38803 3.07698C3.82354 3.3646 3.3646 3.82354 3.07698 4.38803L4.85899 5.29601ZM19.75 11H9.75V13H19.75V11ZM10.75 12V3.75H8.75V12H10.75ZM8.75 12V20.25H10.75V12H8.75ZM19.25 11.5C19.25 11.2239 19.4739 11 19.75 11V13C20.5784 13 21.25 12.3284 21.25 11.5H19.25Z"
                                    className="fill-current stroke-transparent"></path>
                                <path
                                    d="M18 15V18M18 18V21M18 18H15M18 18H21"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="fill-transparent stroke-current"></path>
                            </svg>
                        </div>
                        <div className="whitespace-nowrap">{lang.workspace.addModule}</div>
                    </Fragment>
                )}

                {isIconOnly && (
                    <Fragment>
                        <div className="w-5 h-5">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-ds-icon-button-xs ms-ds-0 me-ds-0 mt-ds-0 mb-ds-0"
                                name="LayoutAdd">
                                <path
                                    d="M12.05 21.25C12.6023 21.25 13.05 20.8023 13.05 20.25C13.05 19.6977 12.6023 19.25 12.05 19.25V21.25ZM4.84202 20.032L5.29601 19.141L5.29601 19.141L4.84202 20.032ZM3.96799 19.158L4.85899 18.704L4.85899 18.704L3.96799 19.158ZM20.032 4.84202L19.141 5.29601L19.141 5.29601L20.032 4.84202ZM19.158 3.96799L18.704 4.85899L18.704 4.85899L19.158 3.96799ZM3.96799 4.84202L3.07698 4.38803L3.07698 4.38803L3.96799 4.84202ZM4.84202 3.96799L4.38803 3.07698L4.38803 3.07698L4.84202 3.96799ZM10.75 3.75V2.75H8.75V3.75H10.75ZM8.75 20.25V21.25H10.75V20.25H8.75ZM19.25 6.95V11.5H21.25V6.95H19.25ZM12.05 19.25H6.95V21.25H12.05V19.25ZM4.75 17.05V6.95H2.75V17.05H4.75ZM6.95 4.75H17.05V2.75H6.95V4.75ZM6.95 19.25C6.37345 19.25 6.00117 19.2492 5.71784 19.2261C5.44617 19.2039 5.34545 19.1662 5.29601 19.141L4.38803 20.923C4.76641 21.1158 5.15963 21.1871 5.55497 21.2194C5.93864 21.2508 6.40645 21.25 6.95 21.25V19.25ZM2.75 17.05C2.75 17.5935 2.74922 18.0614 2.78057 18.445C2.81287 18.8404 2.88419 19.2336 3.07698 19.612L4.85899 18.704C4.8338 18.6545 4.79612 18.5538 4.77393 18.2822C4.75078 17.9988 4.75 17.6265 4.75 17.05H2.75ZM5.29601 19.141C5.10785 19.0451 4.95487 18.8921 4.85899 18.704L3.07698 19.612C3.3646 20.1765 3.82354 20.6354 4.38803 20.923L5.29601 19.141ZM21.25 6.95C21.25 6.40645 21.2508 5.93864 21.2194 5.55497C21.1871 5.15963 21.1158 4.76641 20.923 4.38803L19.141 5.29601C19.1662 5.34545 19.2039 5.44617 19.2261 5.71784C19.2492 6.00117 19.25 6.37345 19.25 6.95H21.25ZM17.05 4.75C17.6266 4.75 17.9988 4.75078 18.2822 4.77393C18.5538 4.79612 18.6545 4.8338 18.704 4.85899L19.612 3.07698C19.2336 2.88419 18.8404 2.81287 18.445 2.78057C18.0614 2.74922 17.5936 2.75 17.05 2.75V4.75ZM20.923 4.38803C20.6354 3.82354 20.1765 3.3646 19.612 3.07698L18.704 4.85899C18.8922 4.95487 19.0451 5.10785 19.141 5.29601L20.923 4.38803ZM4.75 6.95C4.75 6.37345 4.75078 6.00117 4.77393 5.71784C4.79612 5.44617 4.8338 5.34545 4.85899 5.29601L3.07698 4.38803C2.88419 4.76641 2.81287 5.15963 2.78057 5.55497C2.74922 5.93864 2.75 6.40645 2.75 6.95H4.75ZM6.95 2.75C6.40645 2.75 5.93864 2.74922 5.55497 2.78057C5.15963 2.81287 4.76641 2.88419 4.38803 3.07698L5.29601 4.85899C5.34545 4.8338 5.44617 4.79612 5.71784 4.77393C6.00117 4.75078 6.37345 4.75 6.95 4.75V2.75ZM4.85899 5.29601C4.95487 5.10785 5.10785 4.95487 5.29601 4.85899L4.38803 3.07698C3.82354 3.3646 3.3646 3.82354 3.07698 4.38803L4.85899 5.29601ZM19.75 11H9.75V13H19.75V11ZM10.75 12V3.75H8.75V12H10.75ZM8.75 12V20.25H10.75V12H8.75ZM19.25 11.5C19.25 11.2239 19.4739 11 19.75 11V13C20.5784 13 21.25 12.3284 21.25 11.5H19.25Z"
                                    className="fill-current stroke-transparent"></path>
                                <path
                                    d="M18 15V18M18 18V21M18 18H15M18 18H21"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="fill-transparent stroke-current"></path>
                            </svg>
                        </div>
                        {!isMobile && <div className="whitespace-nowrap">{lang.workspace.addModule}</div>}
                    </Fragment>
                )}
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95">
                <Menu.Items
                    className={cn(
                        'absolute z-10 mt-2 min-w-40 w-48 origin-top-right divide-y divide-white/10 bg-brand-background-dark border border-brand-neutral-700 overflow-hidden right-0',
                        roundedDropdownClassname
                    )}>
                    {tabList.map((tab) => (
                        <MenuItemButton
                            key={tab}
                            label={getModuleTitle(tab as XplorSpotModules)}
                            Icon={tabIcon[tab]}
                            onClick={(e) => {
                                e.preventDefault();
                                if (onClick) onClick(tab);
                                else {
                                    if (platform === 'xplorspot') {
                                        addModuleToXplorSpotWorkspace(`${tab}-${uuidv4()}` as XplorSpotModules);
                                    } else {
                                        addModuleToXplorTraderWorkspace(`${tab}-${uuidv4()}` as XplorTraderModules);
                                    }
                                }
                            }}
                        />
                    ))}
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

export default AddModule;
